package com.essntl.features.country.ui.addeditcountry

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.destructive.EsLargeDestructiveButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@Composable
fun AddEditCountryScreen(
    state: AddEditCountryState,
    validation: AddEditCountryValidation,
    onEvent: (AddEditCountryEvent) -> Unit,
) {
    val isEdit by remember(state.country.value.id) {
        mutableStateOf(
            state.country.value.id.isNotBlank(),
        )
    }

    var isEditEnabled by remember(state.getCountry.data?.id) { mutableStateOf(false) }

    LaunchedEffect(state.addEditCountry.isSuccess) {
        if (state.addEditCountry.isSuccess) {
            isEditEnabled = false
        }
    }

    LaunchedEffect(state.getCountry.data) {
        validation.addEditCountryForm.reset()
        validation.addEditCountryForm.name.value = state.getCountry.data?.name.orEmpty()
    }

    LazyColumn(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .fillMaxSize()
            .clip(
                RoundedCornerShape(
                    topStart = 24.dp,
                    topEnd = 24.dp,
                ),
            )
            .padding(
                horizontal = 2.5f.spacing,
            ),
    ) {
        if (
            state.getCountry.isLoading ||
            state.getCountry.isFailure ||
            (isEdit && state.getCountry.data == null)
        )
            item {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                    Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(2.5f.spacing)
                        .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                ) {
                    if (state.getCountry.isLoading) {
                        EsSpinner()
                    } else {
                        Text(
                            text = "Something goes wrong!",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p1Medium16,
                        )
                    }
                }
            }
        else {
            item {
                EsInput(
                    value = state.country.value.name,
                    state = if (!state.addEditCountry.errorMessage.isNullOrEmpty())
                        EsInputState.Error else
                        validation.addEditCountryForm.name.state,
                    onValueChange = {
                        validation.addEditCountryForm.name.validate(it)
                        onEvent(
                            AddEditCountryEvent.UpdateCountryFields(
                                state.country.value.copy(
                                    name = it,
                                ),
                            ),
                        )
                    },
                    label = "Title",
                    placeholder = "Enter title",
                    message = state.addEditCountry.errorMessage
                        .orEmpty()
                        .ifEmpty { "This field is required" },
                    enabled = if (isEdit) isEditEnabled else true,
                )
            }

            item {
                Spacer(modifier = Modifier.height(3.spacing))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    if (isEdit)
                        EsLargeDestructiveButton(
                            onClick = {
                                onEvent(AddEditCountryEvent.DeleteCountry)
                            },
                            text = "Delete Country",
                            suffixIconRes = EsIcons.Trash,
                            loading = state.getAreasCountryCount.isLoading || state.deleteCountry.isLoading,
                            enabled = state.getAreasCountryCount.data?.toInt() == 0,
                            modifier = Modifier.weight(1f),
                        )

                    EsLargePrimaryButton(
                        onClick = {
                            if (isEdit && !isEditEnabled) {
                                isEditEnabled = true
                            } else if (validation.addEditCountryForm.validate()) {
                                if (isEdit) {
                                    onEvent(AddEditCountryEvent.EditCountry)
                                } else {
                                    onEvent(AddEditCountryEvent.AddCountry)
                                }
                            }
                        },
                        text =
                        if (isEdit) {
                            if (isEditEnabled) {
                                "Update"
                            } else {
                                "Edit"
                            }
                        } else {
                            "Save Country"
                        },
                        loading = state.addEditCountry.isLoading,
                        enabled = if (isEditEnabled)
                            (state.isEditEnabled) else
                            true,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
        }
    }
}
